import { now, user } from 'src/StateObserver';
import { withLoading } from 'src/lib/utils';
import {
  buddiesGetEvent,
  buddiesGetUnseenPartyJoinCount,
  buddiesIsDummyPlayer,
  buddiesGetMVP,
} from 'src/replicant/buddies/buddies.getters';
import {
  buddiesDummyPrefix,
  buddiesMilestoneThresholds,
} from 'src/replicant/buddies/buddies.ruleset';
import { BuddiesEvent } from 'src/replicant/buddies/buddies.schema';
import { PetSpeciesID } from 'src/replicant/pet';
import * as Pet from 'src/replicant/pet';
import { buddiesGetPlayers } from './buddies.friends';
import {
  BuddiesPlayer,
  BuddiesPlayerMap,
} from 'src/replicant/buddies/buddies.asyncGetters';
import { GENERIC_AVATAR } from 'src/replicant/profile/assets';
import { species as petSpeciesList } from 'src/replicant/pet/pet.ruleset';
import { createPrng } from '@play-co/replicant';
import { getPlayerProfile } from 'src/replicant/profile/profile.getters';
import i18n from 'src/lib/i18n/i18n';
import { GCInstant } from 'src/lib/gcinstant';
import { Feature } from 'src/replicant/analytics';

/**
 * Type for players in the party, used by UI
 */
export type BuddiesPartyPlayer = {
  id: string;
  name: string;
  score: number;
  scoreAdded: number;
  photo: string;
  species: PetSpeciesID;
  mvp: boolean;
};

/**
 * Get the progress stage of the event, for visual purposes.
 */
export function buddiesGetStage(score: number) {
  const nextMilestone = buddiesMilestoneThresholds.findIndex((m) => score < m);
  const stage =
    nextMilestone === -1 ? buddiesMilestoneThresholds.length : nextMilestone;
  return {
    stage: stage + 1,
    numStages: buddiesMilestoneThresholds.length + 1,
  };
}

/**
 * Get party players with all info needed by the feature: scores come from the event state,
 * while player name, photo and species are fetched via buddiesGetPlayers.
 */
export async function buddiesGetPartyPlayers(
  eventId: string,
): Promise<BuddiesPartyPlayer[]> {
  const event = user().bakingBuddies?.events?.[eventId];
  if (!event) return [];

  const mainUserId = user().id;

  // Get party member IDs, excluding the main user
  const partyIds = Object.keys(event.party).filter((id) => id !== mainUserId);

  // Get dummy and real player IDs separately
  const dummyIds = partyIds.filter((id) => buddiesIsDummyPlayer(id));
  const realIds = partyIds.filter((id) => !buddiesIsDummyPlayer(id));

  // Map of all players
  const allPlayers: BuddiesPlayerMap = {};

  // Create synthetic entries for dummy players (debug cheats)
  for (const id of dummyIds) {
    allPlayers[id] = buddiesCreateDummyPlayer(id);
  }

  // Fetch profiles only for real party members (not dummies), cached per ID set
  if (realIds.length) {
    const fetchedPlayers = await withLoading(
      () => buddiesGetPlayers(realIds).get(),
      {
        feature: Feature.BUDDIES._,
        subFeature: Feature.BUDDIES._,
      },
    );
    Object.assign(allPlayers, fetchedPlayers);
  }

  const players = partyIds.map((id) => {
    const player = allPlayers[id] ?? buddiesCreatePlaceholderPlayer(id);
    return buddiesGetPartyPlayer(event, player);
  });

  // Add the main user to the party
  players.unshift(buddiesGetMainPartyPlayer(event));

  // Define the mvp player from the list
  const mvpPlayer = buddiesGetMVP(players);
  if (mvpPlayer) mvpPlayer.mvp = true;

  // Return the players
  return players;
}

/**
 * Get BuddiesPartyPlayer object from user state
 */
function buddiesGetMainPartyPlayer(event: BuddiesEvent): BuddiesPartyPlayer {
  const state = user();
  const species = Pet.getters.getCurrentEvolution(state)?.species;
  const profile = getPlayerProfile(
    state,
    state.publicProfile?.name,
    state.publicProfile?.photo,
    state.profile?.name,
    state.profile?.photo,
  );
  return {
    id: state.id,
    score: event.score,
    scoreAdded: event.lastScoreAdded ?? 0,
    name: i18n('basic.you'),
    photo: profile.photo,
    species: species ?? 'ball',
    mvp: false,
  };
}

/**
 * Get BuddiesPartyPlayer object for a given player
 */
function buddiesGetPartyPlayer(
  event: BuddiesEvent,
  player: BuddiesPlayer,
): BuddiesPartyPlayer {
  const partyState = event.party[player.id];
  return {
    id: player.id,
    score: partyState?.score ?? 0,
    scoreAdded: partyState?.lastScoreAdded ?? 0,
    name: player.name,
    photo: player.photo,
    species: player.species,
    mvp: false,
  };
}

/**
 * Get the IDs of party members that joined since the owner last acknowledged.
 * Returns the N newest members sorted by updatedAt descending.
 */
export function buddiesGetUnseenPartyJoinerIds(): string[] {
  const event = buddiesGetEvent(user(), now());
  if (!event) return [];

  const unseenCount = buddiesGetUnseenPartyJoinCount(user(), now());
  if (unseenCount <= 0) return [];

  return Object.entries(event.party)
    .filter(([id]) => !buddiesIsDummyPlayer(id))
    .sort(([, a], [, b]) => b.updatedAt - a.updatedAt)
    .slice(0, unseenCount)
    .map(([id]) => id);
}

/**
 * Create a synthetic BuddiesPlayer for a dummy player ID.
 * Species is deterministically derived from the ID via PRNG.
 */
function buddiesCreateDummyPlayer(id: string): BuddiesPlayer {
  const index =
    parseInt(id.slice(buddiesDummyPrefix.length).split('-')[0], 10) || 1;
  const rng = createPrng(id);
  return {
    id,
    name: `Dummy ${index}`,
    photo: GENERIC_AVATAR,
    species: petSpeciesList[
      Math.floor(rng() * petSpeciesList.length)
    ] as PetSpeciesID,
  };
}

/**
 * Create a placeholder BuddiesPlayer for a party member whose profile failed to load.
 */
function buddiesCreatePlaceholderPlayer(id: string): BuddiesPlayer {
  const profile = getPlayerProfile(user(), undefined, undefined);
  return {
    id,
    name: profile.name,
    photo: profile.photo,
    species: 'ball',
  };
}

/**
 * Determine whether the user entered via a Buddies share URL.
 */
const buddiesShareEntrySubFeatures = new Set<string>([
  Feature.BUDDIES.LINE_SHARE,
  Feature.BUDDIES.NATIVE_SHARE,
  Feature.BUDDIES.SHARE_INVITE,
  // ─── Legacy ───
  // In-flight links from older clients still carry these old subFeature
  // strings — kept here so they continue to trigger the buddies invite flow.
  Feature.BUDDIES.LEGACY_LINE_SHARE,
  Feature.BUDDIES.LEGACY_NATIVE_SHARE,
]);

export function buddiesIsEnteringFromInvite(): boolean {
  const sub =
    GCInstant.entryData?.$subFeature ?? GCInstant.entryData?.subFeature;
  return !!sub && buddiesShareEntrySubFeatures.has(sub);
}
